/**
 * Shared store helpers.
 *
 * These were previously duplicated inside OrderStores.tsx / Home.tsx /
 * Businesses.tsx. Behaviour is copied verbatim from OrderStores.tsx,
 * which had the most complete implementation — no logic changes.
 */

export type StoreItem = {
  id: number; slug?: string | null; name: string; description?: string | null;
  category: string; categories?: string[] | null; town: string; imageUrl?: string | null; bannerUrl?: string | null;
  coverMode?: string; coverAnimationUrl?: string | null; coverPosterUrl?: string | null; coverVideoUrl?: string | null;
  offersDelivery: boolean; offersCollection: boolean;
  deliveryFeePence: number; minOrderPence: number;
  deliveryTimeMins: number; collectionTimeMins: number;
  currentDeliveryMins?: number | null; currentCollectionMins?: number | null;
  acceptingOrders?: boolean | null;
  isOpen?: boolean | null; closesAt?: string | null; opensAt?: string | null; nextOpenTime?: string | null;
  sponsored?: boolean | null; sponsoredUntil?: string | null;
  stripeOnboardingComplete?: boolean | null;
  hasDeliveryZones?: boolean;
  deliveryFeeLabel?: string; quotedDeliveryFeePence?: number; deliveryUnavailable?: boolean;
  /** Set by the API when an order_store_boosts row is live for the town being browsed. */
  isBoosted?: boolean;
  activeOffers?: Array<{ title: string; type: string; value: number; minOrderPence: number; scoped?: boolean }>;
  /** "Any 3 for £10" style deals. Separate from activeOffers, which are auto-apply discounts. */
  mixMatchDeals?: Array<{ name: string; qty: number; pricePence: number }>;
  reviewCount?: number;
  avgRating?: number;
  multiStoreEligible?: boolean;
};

/** Format integer pence as £x.xx. Never use floats for money (Platform LAW 5). */
export function pence(p: number): string {
  return `£${(p / 100).toFixed(2)}`;
}

/**
 * There are two sponsorship mechanisms and this only knew about the dead one.
 *
 * `sponsored` / `sponsoredUntil` are legacy columns on order_stores and are
 * false for every store on the platform. Paid promotion actually runs through
 * order_store_boosts, which the API evaluates per town and returns as
 * `isBoosted` — and which this ignored, so a shop that had paid for a boost
 * sorted as though it had not, and the API's own boost ordering was discarded
 * by the client re-sort.
 *
 * Both are honoured now so the legacy flag keeps working if it is ever set.
 */
export function isSponsored(store: StoreItem): boolean {
  if (store.isBoosted) return true;
  if (!store.sponsored) return false;
  if (!store.sponsoredUntil) return true;
  return new Date(store.sponsoredUntil) > new Date();
}

export function storeIsOpen(store: StoreItem): boolean {
  return store.acceptingOrders !== false && store.isOpen !== false;
}

/**
 * "Opens today at 5:00pm".
 *
 * Europe/London explicitly, because the server sends UTC timestamps and a
 * customer in BST must not be told a shop opens an hour early. This is the
 * implementation OrderStores.tsx and mobile `order.tsx` already shared; the
 * earlier version here used the browser's local zone and shorter wording,
 * so the two disagreed.
 */
export function formatNextOpen(
  nextOpenTime: string | null | undefined,
  acceptingOrders?: boolean | null,
): string {
  if (acceptingOrders === false) return "Temporarily closed";
  if (!nextOpenTime) return "Closed";
  const date = new Date(nextOpenTime);
  if (Number.isNaN(date.getTime())) return "Closed";
  const timeStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", hour: "numeric", minute: "2-digit", hour12: true,
  }).format(date).replace(" am", "am").replace(" pm", "pm");
  const nowLocal = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(new Date());
  const openLocal = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(date);
  const diffDays = Math.round((new Date(openLocal).getTime() - new Date(nowLocal).getTime()) / 86400000);
  if (diffDays === 0) return `Opens today at ${timeStr}`;
  if (diffDays === 1) return `Opens tomorrow at ${timeStr}`;
  const dayName = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", weekday: "long" }).format(date);
  return `Opens ${dayName} at ${timeStr}`;
}

/**
 * Offer type strings as the API actually emits them.
 *
 * The DB column is `order_store_offers.type`, validated server-side against
 * exactly these four values (api-server/src/routes/order.ts:5705). An earlier
 * version of this file guessed at `percent_off` / `amount_off`, which match
 * nothing the API sends — every percentage and fixed offer fell through to the
 * default and rendered as a bare "Offer" with the discount amount dropped.
 */
export type OfferType = "percentage" | "fixed" | "free_delivery" | "free_item";

export function formatOfferLabel(offer: { type: string; value: number; bogofBuyQty?: number; bogofFreeQty?: number }): string {
  switch (offer.type) {
    case "percentage":    return `${offer.value}% off`;
    case "fixed":         return `${pence(offer.value)} off`;
    case "free_item":     return "Free item";
    case "free_delivery": return "Free delivery";
    case "bogof":         return `Buy ${offer.bogofBuyQty ?? 2} get ${offer.bogofFreeQty ?? 1} cheapest free`;
    default: return "Offer";
  }
}

/**
 * Badge label for an offer. Scoped offers (discount limited to certain items/
 * sections) show the shop's own title (e.g. "50% OFF CREPES") so they don't read
 * as a whole-store deal; whole-menu offers use the generated label.
 */
export function offerDisplayLabel(offer: { type: string; value: number; title?: string; scoped?: boolean; bogofBuyQty?: number; bogofFreeQty?: number }): string {
  if (offer.scoped && offer.title && offer.title.trim()) return offer.title.trim();
  return formatOfferLabel(offer);
}

/** Retained for existing consumers; promotions use typography instead of emoji. */
export function offerIcon(_type: string): string { return ""; }

/** Offer label including the minimum-spend qualifier, when there is one. */
export function formatOfferWithMin(
  offer: { type: string; value: number; minOrderPence: number; title?: string; scoped?: boolean },
): string {
  const base = offerDisplayLabel(offer);
  return offer.minOrderPence > 0 ? `${base} · min ${pence(offer.minOrderPence)}` : base;
}

/** Open stores first, then sponsored, then by name. */
export function sortStores(stores: StoreItem[]): StoreItem[] {
  return [...stores].sort((a, b) => {
    const aOpen = storeIsOpen(a), bOpen = storeIsOpen(b);
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    const aSp = isSponsored(a), bSp = isSponsored(b);
    if (aSp !== bSp) return aSp ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

/** Canonical route for a store. */
export function storeHref(store: Pick<StoreItem, "id" | "slug">): string {
  return `/order/${store.slug ?? store.id}`;
}

/** True when delivery is genuinely free (no zone-based pricing). */
export function hasFreeDelivery(store: StoreItem): boolean {
  return store.offersDelivery && !store.deliveryUnavailable && (store.quotedDeliveryFeePence === 0 || (!store.hasDeliveryZones && store.deliveryFeePence === 0));
}

/* ── Minimum spend ────────────────────────────────────────────────────
   Shops can set a base minimum plus per-daypart overrides.              */

export type MinOrderConfig = {
  minOrderPence: number;
  minOrderBreakfastPence?: number | null;
  minOrderLunchPence?: number | null;
  minOrderEveningPence?: number | null;
};

/**
 * The minimum order value that applies at a given moment.
 *
 * Must stay behaviourally identical to `effectiveMinOrderPence` in
 * artifacts/api-server/src/routes/order.ts — the server is authoritative, and a
 * disagreement means either the checkout button enables and the server then
 * rejects, or the button blocks a basket the server would have accepted.
 *
 * A daypart column that is set — INCLUDING 0 — overrides the base. 0 means the
 * shop explicitly wants no minimum in that window; only null means "this window
 * is not configured, use the base". Do not collapse this back into a `> 0`
 * test. Windows are 07:00–11:00 breakfast, 11:00–15:00 lunch, 15:00 onwards
 * evening; outside those (00:00–07:00) the base applies.
 *
 * Evaluated in Europe/London, not the browser's zone — the shop's dayparts are
 * its own opening hours, and a customer whose device is set to another
 * timezone must not be quoted a different minimum from the one enforced.
 *
 * `at` exists so a SCHEDULED order can be evaluated against its delivery time
 * rather than the moment the basket happens to be open.
 */
export function effectiveMinOrderPence(store: MinOrderConfig, at: Date = new Date()): number {
  const h = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London", hour: "numeric", hourCycle: "h23",
    }).format(at),
  );
  if (h >= 7 && h < 11 && store.minOrderBreakfastPence != null) return store.minOrderBreakfastPence;
  if (h >= 11 && h < 15 && store.minOrderLunchPence != null) return store.minOrderLunchPence;
  if (h >= 15 && store.minOrderEveningPence != null) return store.minOrderEveningPence;
  return store.minOrderPence;
}

/* ── Distance ─────────────────────────────────────────────────────────
   Ported verbatim from mobile app/(tabs)/order.tsx so the same store shows
   the same distance on both platforms.                                  */

/** Great-circle distance in miles. */
export function haversineMi(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** "480 yds" under a tenth of a mile, otherwise "1.3 mi". */
export function formatDistance(mi: number): string {
  return mi < 0.1 ? `${Math.round(mi * 1760)} yds` : `${mi.toFixed(1)} mi`;
}

/* ── Catalogue mode ───────────────────────────────────────────────────
   Grocery vs takeaway, derived — there is NO storeType column.          */

/**
 * True when a shop should browse as a *catalogue* rather than a *menu*.
 *
 * Groceries and convenience shops list hundreds of near-identical SKUs with
 * no descriptions; takeaways list a few dozen dishes that each deserve a
 * photo and a sentence. Rendering one as the other is what makes a menu feel
 * like a spreadsheet or a grocery list feel endless.
 *
 * There is no `storeType` column, so this is derived from data that already
 * exists — exactly as mobile `order-store.tsx:1362` does it:
 *   • `subcategoriesEnabled` — the parent → child drill-down groceries opt into
 *   • or a large (>=120 item), description-light (<30% described) catalogue
 *
 * It only ever changes DENSITY and the default view mode. It never affects
 * pricing, availability, options or any cart behaviour.
 */
export function deriveCatalogueMode(
  items: Array<{ description?: string | null }>,
  subcategoriesEnabled: boolean,
): boolean {
  if (subcategoriesEnabled) return true;
  if (items.length < 120) return false;
  const described = items.filter((i) => (i.description ?? "").trim().length > 0).length;
  return described / items.length < 0.3;
}
